var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import UITypes from '../../../lib/UITypes';
import { isSystemColumn } from '../../../lib/helperFunctions';
import { VariableGroupKey, VariableType, } from '../../../lib/workflow/interface';
import { RelationTypes } from '../../../lib/globals';
import { FormulaDataTypes } from '../../../lib/formula/enums';
import { prefixVariableKeys } from '../../../ee/lib';
/**
 * Map UIType to icon name (matching NocoDB's iconMap)
 */
export function uiTypeToIcon(column) {
    switch (column.uidt) {
        case UITypes.ID:
        case UITypes.ForeignKey:
            return 'cellSystemKey';
        case UITypes.SingleLineText:
            return 'cellText';
        case UITypes.LongText:
            return 'cellLongText';
        case UITypes.Email:
            return 'cellEmail';
        case UITypes.URL:
            return 'cellUrl';
        case UITypes.PhoneNumber:
            return 'cellPhone';
        case UITypes.Number:
            return 'cellNumber';
        case UITypes.Decimal:
            return 'cellDecimal';
        case UITypes.Currency:
            return 'cellCurrency';
        case UITypes.Percent:
            return 'cellPercent';
        case UITypes.Duration:
            return 'cellDuration';
        case UITypes.Rating:
            return 'cellRating';
        case UITypes.Checkbox:
            return 'cellCheckbox';
        case UITypes.Date:
            return 'cellDate';
        case UITypes.DateTime:
            return 'cellDatetime';
        case UITypes.Time:
            return 'cellTime';
        case UITypes.Year:
            return 'cellYear';
        case UITypes.Attachment:
            return 'cellAttachment';
        case UITypes.SingleSelect:
            return 'cellSingleSelect';
        case UITypes.MultiSelect:
            return 'cellMultiSelect';
        case UITypes.Collaborator:
        case UITypes.User:
            return 'cellUser';
        case UITypes.CreatedBy:
        case UITypes.LastModifiedBy:
            return 'cellSystemUser';
        case UITypes.CreatedTime:
        case UITypes.LastModifiedTime:
            return 'cellSystemDate';
        case UITypes.LinkToAnotherRecord:
        case UITypes.Links: {
            const relationType = column.colOptions.type;
            if (relationType === RelationTypes.HAS_MANY) {
                return 'hm_solid';
            }
            else if (relationType === RelationTypes.MANY_TO_MANY) {
                return 'mm_solid';
            }
            else if (relationType === RelationTypes.BELONGS_TO) {
                return 'bt_solid';
            }
            else if (relationType === RelationTypes.ONE_TO_ONE) {
                return 'oneToOneSolid';
            }
            return 'mmSolid';
        }
        case UITypes.Lookup:
            return 'cellLookup';
        case UITypes.Rollup:
            return 'cellRollup';
        case UITypes.Formula:
            return 'cellFormula';
        case UITypes.Count:
        case UITypes.AutoNumber:
            return 'cellNumber';
        case UITypes.Barcode:
            return 'cellBarcode';
        case UITypes.QrCode:
            return 'cellQrCode';
        case UITypes.GeoData:
            return 'ncMapPin';
        case UITypes.Geometry:
            return 'cellGeometry';
        case UITypes.JSON:
            return 'cellJson';
        case UITypes.Button:
            return 'cellButton';
        case UITypes.SpecificDBType:
            return 'cellDb';
        default:
            return 'cellSystemText';
    }
}
/**
 * Map Formula data types to VariableType
 */
function formulaDataTypeToVariableType(formulaDataType) {
    switch (formulaDataType) {
        case FormulaDataTypes.NUMERIC:
            return VariableType.Number;
        case FormulaDataTypes.STRING:
            return VariableType.String;
        case FormulaDataTypes.DATE:
            return VariableType.DateTime;
        case FormulaDataTypes.BOOLEAN:
        case FormulaDataTypes.LOGICAL:
            return VariableType.Boolean;
        case FormulaDataTypes.COND_EXP:
            return VariableType.String;
        case FormulaDataTypes.NULL:
        case FormulaDataTypes.UNKNOWN:
            return VariableType.String;
        default:
            return VariableType.Object; // Fallback for unknown types
    }
}
/**
 * Convert NocoDB UIType to VariableType
 * Note: For Lookup fields, this returns a generic Object type.
 * Use resolveLookupType() for accurate type resolution.
 */
export function uiTypeToVariableType(uiType, columnMeta) {
    switch (uiType) {
        // Formula - resolve actual type from parsed tree
        case UITypes.Formula: {
            const parsedTree = columnMeta === null || columnMeta === void 0 ? void 0 : columnMeta.parsed_tree;
            if (parsedTree === null || parsedTree === void 0 ? void 0 : parsedTree.dataType) {
                const formulaType = formulaDataTypeToVariableType(parsedTree.dataType);
                return { type: formulaType, isArray: false };
            }
            // Fallback if no parsed tree available
            return { type: VariableType.String, isArray: false };
        }
        // Number types
        case UITypes.Number:
        case UITypes.Decimal:
        case UITypes.Currency:
        case UITypes.Percent:
        case UITypes.Duration:
        case UITypes.Rating:
        case UITypes.Rollup:
        case UITypes.AutoNumber:
        case UITypes.Count:
            return { type: VariableType.Number, isArray: false };
        // Integer type
        case UITypes.Links:
            return { type: VariableType.Integer, isArray: false };
        // Boolean types
        case UITypes.Checkbox:
            return { type: VariableType.Boolean, isArray: false };
        // DateTime types
        case UITypes.Date:
        case UITypes.DateTime:
        case UITypes.Time:
        case UITypes.CreatedTime:
        case UITypes.LastModifiedTime:
            return { type: VariableType.DateTime, isArray: false };
        // Array types
        case UITypes.Attachment:
            return { type: VariableType.Object, isArray: true };
        case UITypes.MultiSelect:
            return { type: VariableType.String, isArray: true };
        // Object types
        case UITypes.LinkToAnotherRecord: {
            // Check relation type from columnMeta
            const isMultiple = (columnMeta === null || columnMeta === void 0 ? void 0 : columnMeta.type) === RelationTypes.HAS_MANY ||
                (columnMeta === null || columnMeta === void 0 ? void 0 : columnMeta.type) === RelationTypes.MANY_TO_MANY;
            return { type: VariableType.Object, isArray: isMultiple };
        }
        case UITypes.User:
        case UITypes.Collaborator: {
            return { type: VariableType.Object, isArray: true };
        }
        case UITypes.LastModifiedBy:
        case UITypes.CreatedBy:
            return { type: VariableType.Object, isArray: false };
        case UITypes.JSON:
            return { type: VariableType.Object, isArray: false };
        case UITypes.Lookup: {
            return { type: VariableType.Object, isArray: false };
        }
        // String types (default)
        case UITypes.SingleLineText:
        case UITypes.LongText:
        case UITypes.Email:
        case UITypes.URL:
        case UITypes.PhoneNumber:
        case UITypes.SingleSelect:
        case UITypes.Year:
        case UITypes.GeoData:
        case UITypes.Geometry:
        case UITypes.Barcode:
        case UITypes.QrCode:
        case UITypes.Button:
        case UITypes.SpecificDBType:
        case UITypes.ID:
        case UITypes.ForeignKey:
        default:
            return { type: VariableType.String, isArray: false };
    }
}
/**
 * Recursively resolve the actual type of a Lookup field
 * This follows the same logic as the Swagger V3 generator
 */
function resolveLookupType(column_1, context_1) {
    return __awaiter(this, arguments, void 0, function* (column, context, visitedColumnIds = new Set()) {
        // Prevent infinite recursion
        if (visitedColumnIds.has(column.id)) {
            return { type: VariableType.Object, isArray: false };
        }
        visitedColumnIds.add(column.id);
        const lookupOptions = column.colOptions;
        if (!lookupOptions) {
            return { type: VariableType.Object, isArray: false };
        }
        try {
            // Get the relation column to determine if it's an array
            const relationColumnId = lookupOptions.fk_relation_column_id;
            const lookupColumnId = lookupOptions.fk_lookup_column_id;
            if (!relationColumnId || !lookupColumnId || !(context === null || context === void 0 ? void 0 : context.getColumn)) {
                // Fallback if we can't resolve
                return { type: VariableType.Object, isArray: false };
            }
            // Get the relation column
            const relationColumn = yield context.getColumn(relationColumnId);
            const relationOptions = relationColumn === null || relationColumn === void 0 ? void 0 : relationColumn.colOptions;
            // Determine if this is an array lookup based on relation type
            const isArrayRelation = (relationOptions === null || relationOptions === void 0 ? void 0 : relationOptions.type) === RelationTypes.HAS_MANY ||
                (relationOptions === null || relationOptions === void 0 ? void 0 : relationOptions.type) === RelationTypes.MANY_TO_MANY;
            // Get the lookup target column
            const lookupColumn = yield context.getColumn(lookupColumnId);
            if (!lookupColumn) {
                return { type: VariableType.Object, isArray: isArrayRelation };
            }
            // Recursively resolve the type of the lookup target
            let targetType;
            if (lookupColumn.uidt === UITypes.Lookup) {
                // Nested lookup - recurse
                targetType = yield resolveLookupType(lookupColumn, context, visitedColumnIds);
            }
            else {
                // Base case - get the type of the target column
                targetType = uiTypeToVariableType(lookupColumn.uidt, lookupColumn.colOptions);
            }
            // If the relation is an array, the lookup result is also an array
            // (even if the target column itself is not an array)
            if (isArrayRelation) {
                return { type: targetType.type, isArray: true };
            }
            // Otherwise, return the target type as-is
            return targetType;
        }
        catch (error) {
            // Fallback on error
            return { type: VariableType.Object, isArray: false };
        }
    });
}
/**
 * Escape a property name for use inside single-quoted bracket notation (['...'])
 * Escapes backslashes and single quotes so the key round-trips safely.
 */
export function escapeVariableKey(name) {
    return name.replace(/\\/g, '\\\\').replace(/'/g, "\\'");
}
/**
 * Unescape a property name extracted from single-quoted bracket notation.
 * Reverses escapeVariableKey.
 */
export function unescapeVariableKey(escaped) {
    return escaped.replace(/\\'/g, "'").replace(/\\\\/g, '\\');
}
/** Regex that matches a single-quoted bracket segment at the end of a key, handling escaped chars */
export const BRACKET_KEY_RE = /\['((?:[^'\\]|\\.)*)'\]$/;
/**
 * Helper to safely build a property accessor (dot notation or bracket notation)
 * Uses bracket notation if the property name contains spaces or special characters
 */
function buildPropertyKey(prefix, propertyName) {
    // Check if property name needs bracket notation
    // Use bracket notation if it contains spaces, starts with a number, or has special chars
    const needsBrackets = /[^a-zA-Z0-9_$]/.test(propertyName) || /^\d/.test(propertyName);
    if (needsBrackets) {
        return `${prefix}['${escapeVariableKey(propertyName)}']`;
    }
    else {
        return `${prefix}.${propertyName}`;
    }
}
/**
 * Generate variable definition from NocoDB column
 */
export function getFieldVariable(column_1) {
    return __awaiter(this, arguments, void 0, function* (column, prefix = 'fields', context) {
        let type;
        let isArray;
        let itemType;
        // When linksAsLtar is enabled, treat Links columns as LinkToAnotherRecord
        const effectiveUidt = (context === null || context === void 0 ? void 0 : context.linksAsLtar) && column.uidt === UITypes.Links
            ? UITypes.LinkToAnotherRecord
            : column.uidt;
        if (effectiveUidt === UITypes.Lookup && context) {
            // For Lookup fields, resolve the actual type
            const resolvedType = yield resolveLookupType(column, context);
            if (resolvedType.isArray) {
                type = VariableType.Array;
                isArray = true;
                itemType = resolvedType.type;
            }
            else {
                type = resolvedType.type;
                isArray = false;
            }
        }
        else {
            // For other fields, use the standard type mapping
            const typeInfo = uiTypeToVariableType(effectiveUidt, column.colOptions);
            type = typeInfo.type;
            isArray = typeInfo.isArray;
        }
        const variable = {
            key: buildPropertyKey(prefix, column.title),
            name: column.title,
            type,
            groupKey: VariableGroupKey.Fields,
            isArray,
            extra: {
                entity_id: column.id,
                entity: 'column',
                uiType: effectiveUidt,
                icon: uiTypeToIcon(column),
            },
        };
        // For array Lookup fields, add itemSchema with the item type
        if (column.uidt === UITypes.Lookup && isArray && itemType) {
            variable.extra = Object.assign(Object.assign({}, variable.extra), { itemSchema: [
                    {
                        key: '',
                        name: 'Item',
                        type: itemType,
                        groupKey: VariableGroupKey.Fields,
                        extra: {
                            icon: uiTypeToIcon(column),
                        },
                    },
                ] });
        }
        // Handle LTAR fields - add structure for id and fields properties
        if (effectiveUidt === UITypes.LinkToAnotherRecord) {
            const ltarOptions = column.colOptions;
            const relatedTableId = ltarOptions === null || ltarOptions === void 0 ? void 0 : ltarOptions.fk_related_model_id;
            // Try to get related table's primary value column if context provides it
            let pvFieldVariable;
            if (relatedTableId && (context === null || context === void 0 ? void 0 : context.getTableColumns)) {
                try {
                    const relatedColumns = yield context.getTableColumns(relatedTableId);
                    // Find the primary value (display) column
                    const pvColumn = relatedColumns.find((col) => col.pv) ||
                        relatedColumns.find((col) => col.pk);
                    if (pvColumn) {
                        // Generate field variable for the PV column only
                        pvFieldVariable = yield getFieldVariable(pvColumn, 'fields', context);
                    }
                }
                catch (error) {
                    // If we can't get related columns, continue without PV field
                    pvFieldVariable = undefined;
                }
            }
            const recordStructure = [
                {
                    key: 'id',
                    name: 'ID',
                    type: VariableType.String,
                    groupKey: VariableGroupKey.Fields,
                    extra: {
                        icon: 'cellSystemKey',
                    },
                },
                {
                    key: 'fields',
                    name: 'Fields',
                    type: VariableType.Object,
                    groupKey: VariableGroupKey.Fields,
                    extra: {
                        icon: 'cellJson',
                    },
                    children: pvFieldVariable ? [pvFieldVariable] : undefined,
                },
            ];
            if (isArray) {
                // For array LTAR, add itemSchema showing each item has id and fields
                variable.extra = Object.assign(Object.assign({}, variable.extra), { itemSchema: recordStructure });
            }
            else {
                // For single LTAR, add id and fields as children
                variable.children = recordStructure.map((child) => (Object.assign(Object.assign({}, child), { key: `${variable.key}.${child.key}`, children: child.children
                        ? child.children.map((nestedChild) => (Object.assign(Object.assign({}, nestedChild), { key: `${variable.key}.${child.key}.${nestedChild.key}` })))
                        : undefined })));
            }
        }
        if (column.uidt === UITypes.Attachment && isArray) {
            // Define the structure of each attachment item
            variable.extra = Object.assign(Object.assign({}, variable.extra), { itemSchema: [
                    {
                        key: 'url',
                        name: 'URL',
                        type: VariableType.String,
                        groupKey: VariableGroupKey.Fields,
                        extra: {
                            icon: 'cellUrl',
                            description: 'URL',
                        },
                    },
                    {
                        key: 'signedUrl',
                        name: 'Signed URL',
                        type: VariableType.String,
                        groupKey: VariableGroupKey.Fields,
                        extra: {
                            icon: 'cellUrl',
                            description: 'Signed URL',
                        },
                    },
                    {
                        key: 'title',
                        name: 'Title',
                        type: VariableType.String,
                        groupKey: VariableGroupKey.Fields,
                        extra: {
                            icon: 'cellText',
                            description: 'Title',
                        },
                    },
                    {
                        key: 'mimetype',
                        name: 'MimeType',
                        type: VariableType.String,
                        groupKey: VariableGroupKey.Fields,
                        extra: {
                            icon: 'cellText',
                            description: 'MIME type',
                        },
                    },
                    {
                        key: 'size',
                        name: 'Size',
                        type: VariableType.Number,
                        groupKey: VariableGroupKey.Fields,
                        extra: {
                            icon: 'cellNumber',
                            description: 'Size',
                        },
                    },
                ] });
            // Array-level properties
            variable.children = [
                {
                    key: `${variable.key}.length`,
                    name: 'Length',
                    type: VariableType.Number,
                    groupKey: VariableGroupKey.Meta,
                    extra: {
                        description: 'Length',
                        icon: 'cellNumber',
                    },
                },
                {
                    key: `${variable.key}.map(item => item.url).join(', ')`,
                    name: 'URLs of all attachments',
                    type: VariableType.String,
                    groupKey: VariableGroupKey.Fields,
                    extra: {
                        icon: 'cellUrl',
                        description: 'URLs',
                    },
                },
                {
                    key: `${variable.key}.map(item => item.signedUrl).join(', ')`,
                    name: 'Signed URLs of all attachments',
                    type: VariableType.String,
                    groupKey: VariableGroupKey.Fields,
                    extra: {
                        icon: 'cellUrl',
                        description: 'Signed URLs',
                    },
                },
                {
                    key: `${variable.key}.map(item => item.title).join(', ')`,
                    name: 'Titles of all attachments',
                    type: VariableType.String,
                    groupKey: VariableGroupKey.Fields,
                    extra: {
                        icon: 'cellText',
                        description: 'Titles',
                    },
                },
                {
                    key: `${variable.key}.map(item => item.mimetype).join(', ')`,
                    name: 'MimeTypes of all attachments',
                    type: VariableType.String,
                    groupKey: VariableGroupKey.Fields,
                    extra: {
                        icon: 'cellText',
                        description: 'MimeTypes',
                    },
                },
                {
                    key: `${variable.key}.map(item => item.size).join(', ')`,
                    name: 'Sizes of all attachments',
                    type: VariableType.String,
                    groupKey: VariableGroupKey.Fields,
                    extra: {
                        icon: 'cellNumber',
                        description: 'Sizes',
                    },
                },
            ];
        }
        else if (column.uidt === UITypes.User) {
            variable.extra = Object.assign(Object.assign({}, variable.extra), { itemSchema: [
                    {
                        key: 'id',
                        name: 'ID',
                        type: VariableType.String,
                        groupKey: VariableGroupKey.Fields,
                        extra: {
                            icon: 'cellSystemKey',
                            description: 'User ID',
                        },
                    },
                    {
                        key: 'email',
                        name: 'Email',
                        type: VariableType.String,
                        groupKey: VariableGroupKey.Fields,
                        extra: {
                            icon: 'cellEmail',
                            description: 'User email',
                        },
                    },
                    {
                        key: 'display_name',
                        name: 'Display Name',
                        type: VariableType.String,
                        groupKey: VariableGroupKey.Fields,
                        extra: {
                            icon: 'cellText',
                            description: 'User display name',
                        },
                    },
                ] });
            variable.children = [
                {
                    key: `${variable.key}.map(item => item.id).join(', ')`,
                    name: 'User IDs',
                    type: VariableType.String,
                    groupKey: VariableGroupKey.Fields,
                    extra: {
                        icon: 'cellSystemKey',
                        description: 'User IDs',
                    },
                },
                {
                    key: `${variable.key}.map(item => item.email).join(', ')`,
                    name: 'Emails',
                    type: VariableType.String,
                    groupKey: VariableGroupKey.Fields,
                    extra: {
                        icon: 'cellEmail',
                        description: 'Emails',
                    },
                },
                {
                    key: `${variable.key}.map(item => item.display_name || '').join(', ')`,
                    name: 'Display names',
                    type: VariableType.String,
                    groupKey: VariableGroupKey.Fields,
                    extra: {
                        icon: 'cellText',
                        description: 'Display names',
                    },
                },
                {
                    key: `${variable.key}.length`,
                    name: 'Length',
                    type: VariableType.Number,
                    groupKey: VariableGroupKey.Meta,
                    extra: {
                        description: 'Length',
                        icon: 'cellNumber',
                    },
                },
            ];
        }
        else if (column.uidt === UITypes.LastModifiedBy ||
            column.uidt === UITypes.CreatedBy) {
            variable.children = [
                {
                    key: `${variable.key}.id`,
                    name: 'id',
                    type: VariableType.String,
                    groupKey: VariableGroupKey.Fields,
                    extra: {
                        icon: 'cellSystemKey',
                        description: 'User ID',
                    },
                },
                {
                    key: `${variable.key}.email`,
                    name: 'email',
                    type: VariableType.String,
                    groupKey: VariableGroupKey.Fields,
                    extra: {
                        icon: 'cellEmail',
                        description: 'User email',
                    },
                },
                {
                    key: `${variable.key}.display_name`,
                    name: 'display_name',
                    type: VariableType.String,
                    groupKey: VariableGroupKey.Fields,
                    extra: {
                        icon: 'cellText',
                        description: 'User display name',
                    },
                },
            ];
        }
        else if (effectiveUidt === UITypes.LinkToAnotherRecord && isArray) {
            variable.children = [
                {
                    key: `${variable.key}.length`,
                    name: 'Length',
                    type: VariableType.Number,
                    groupKey: VariableGroupKey.Meta,
                    extra: {
                        description: 'Length',
                        icon: 'cellNumber',
                    },
                },
            ];
        }
        else if (column.uidt === UITypes.MultiSelect && isArray) {
            variable.extra = Object.assign(Object.assign({}, variable.extra), { itemSchema: [
                    {
                        key: '', // Empty key means the item itself (it's a primitive string)
                        name: 'option',
                        type: VariableType.String,
                        groupKey: VariableGroupKey.Fields,
                        extra: {
                            icon: 'cellText',
                            description: 'Selected option',
                        },
                    },
                ] });
            // Array-level properties
            variable.children = [
                {
                    key: `${variable.key}.length`,
                    name: 'Length',
                    type: VariableType.Number,
                    groupKey: VariableGroupKey.Meta,
                    extra: {
                        description: 'Length',
                        icon: 'cellNumber',
                    },
                },
            ];
        }
        return variable;
    });
}
/**
 * Generate system/meta variables for records
 */
export function getMetaVariables(prefix = '') {
    const baseKey = prefix ? `${prefix}.` : '';
    return [
        {
            key: `${baseKey}id`,
            name: 'id',
            type: VariableType.String,
            groupKey: VariableGroupKey.Meta,
            extra: {
                description: 'Record ID',
            },
        },
    ];
}
/**
 * Generate complete record variable structure
 * This is the main function for generating variables from NocoDB table columns
 *
 * @param columns - Array of column definitions
 * @param isArray - Whether this represents an array of records
 * @param outputKey - The key under which the record(s) are stored in the output (e.g., 'record', 'deleted', 'records')
 * @param context - Optional context for async column resolution
 */
export function genRecordVariables(columns_1) {
    return __awaiter(this, arguments, void 0, function* (columns, isArray = false, outputKey, context) {
        const filteredColumns = columns.filter((col) => !isSystemColumn(col));
        const recordKey = outputKey || (isArray ? 'records' : 'record');
        const recordName = recordKey.charAt(0).toUpperCase() + recordKey.slice(1);
        if (isArray) {
            // Generate field variables (without array prefix for itemSchema)
            const fieldVariables = yield Promise.all(filteredColumns.map((col) => getFieldVariable(col, 'fields', context)));
            return [
                {
                    key: recordKey,
                    name: recordName,
                    type: VariableType.Array,
                    groupKey: VariableGroupKey.Fields,
                    isArray: true,
                    extra: {
                        description: `List of ${recordName.toLowerCase()}`,
                        icon: 'cellJson',
                        // Define the structure of each record item
                        itemSchema: [
                            {
                                key: 'id',
                                name: 'ID',
                                type: VariableType.String,
                                groupKey: VariableGroupKey.Fields,
                                extra: {
                                    description: 'Record ID',
                                    icon: 'cellSystemKey',
                                },
                            },
                            {
                                key: 'fields',
                                name: 'Fields',
                                type: VariableType.Object,
                                groupKey: VariableGroupKey.Fields,
                                extra: {
                                    description: 'Record fields',
                                    icon: 'cellJson',
                                },
                                children: fieldVariables,
                            },
                        ],
                    },
                    children: [
                        {
                            key: `${recordKey}.length`,
                            name: 'Length',
                            type: VariableType.Number,
                            groupKey: VariableGroupKey.Meta,
                            extra: {
                                description: 'Length',
                                icon: 'cellNumber',
                            },
                        },
                    ],
                },
            ];
        }
        else {
            // Generate field variables with record prefix
            const fieldVariables = yield Promise.all(filteredColumns.map((col) => __awaiter(this, void 0, void 0, function* () {
                const fieldVar = yield getFieldVariable(col, 'fields', context);
                return prefixVariableKeys(fieldVar, recordKey);
            })));
            return [
                {
                    key: recordKey,
                    name: recordName,
                    type: VariableType.Object,
                    groupKey: VariableGroupKey.Fields,
                    extra: {
                        description: `The ${recordName.toLowerCase()}`,
                        icon: 'cellJson',
                    },
                    children: [
                        {
                            key: `${recordKey}.id`,
                            name: 'ID',
                            type: VariableType.String,
                            groupKey: VariableGroupKey.Fields,
                            extra: {
                                description: 'Record ID',
                                icon: 'cellSystemKey',
                            },
                        },
                        {
                            key: `${recordKey}.fields`,
                            name: 'Fields',
                            type: VariableType.Object,
                            groupKey: VariableGroupKey.Fields,
                            extra: {
                                description: 'Record fields',
                                icon: 'cellJson',
                            },
                            children: fieldVariables,
                        },
                    ],
                },
            ];
        }
    });
}
/**
 * Helper function to recursively generate itemSchema for array items
 */
function buildItemSchema(item, extra = {}) {
    if (typeof item !== 'object' || item === null) {
        // Primitive item
        return [
            {
                key: '',
                name: 'item',
                type: typeof item,
                groupKey: VariableGroupKey.Fields,
                isArray: false,
                extra: Object.assign(Object.assign({}, extra), { description: `Individual ${typeof item}` }),
            },
        ];
    }
    // Object item - analyze all properties recursively
    const itemSchema = [];
    for (const [key, value] of Object.entries(item)) {
        const isNestedArray = Array.isArray(value);
        const valueType = typeof value;
        const itemDef = {
            key,
            name: key,
            type: isNestedArray
                ? VariableType.Array
                : valueType === 'object' && value !== null
                    ? VariableType.Object
                    : valueType,
            groupKey: VariableGroupKey.Fields,
            isArray: isNestedArray,
            extra: Object.assign(Object.assign({}, extra), { description: `${key} property` }),
        };
        // Recursively handle nested arrays
        if (isNestedArray && value.length > 0) {
            const nestedItemSchema = buildItemSchema(value[0], extra);
            itemDef.extra = Object.assign(Object.assign({}, itemDef.extra), { itemSchema: nestedItemSchema });
            itemDef.children = [
                {
                    key: `${key}.length`,
                    name: 'Length',
                    type: VariableType.Number,
                    groupKey: VariableGroupKey.Meta,
                    extra: Object.assign(Object.assign({}, extra), { description: 'Number of items' }),
                },
            ];
        }
        else if (valueType === 'object' && value !== null && !isNestedArray) {
            // Recursively handle nested objects
            const nestedChildren = buildItemSchema(value, extra);
            itemDef.children = nestedChildren;
        }
        itemSchema.push(itemDef);
    }
    return itemSchema;
}
/**
 * Generate variables from actual output data (fallback for unknown structures)
 * This is used when we don't have column definitions but have actual output data
 */
export function genGeneralVariables(output, prefix = '', extra = {}) {
    if (output == null)
        return [];
    const variables = [];
    // Primitive value
    if (typeof output !== 'object') {
        return [
            {
                key: prefix || 'value',
                name: prefix || 'value',
                type: typeof output,
                groupKey: VariableGroupKey.Fields,
                extra
            },
        ];
    }
    // Array - analyze first item
    if (Array.isArray(output)) {
        if (output.length === 0)
            return [];
        const firstItem = output[0];
        const arrayName = prefix.split('.').pop() || 'items';
        // Use helper function to recursively build itemSchema
        const itemSchema = buildItemSchema(firstItem, extra);
        return [
            {
                key: prefix,
                name: arrayName,
                type: VariableType.Array,
                groupKey: VariableGroupKey.Fields,
                isArray: true,
                extra: Object.assign(Object.assign({}, extra), { description: `Array of ${arrayName}`, itemSchema: itemSchema }),
                children: [
                    {
                        key: `${prefix}.length`,
                        name: 'Length',
                        type: VariableType.Number,
                        groupKey: VariableGroupKey.Meta,
                        extra: Object.assign(Object.assign({}, extra), { description: 'Number of items' }),
                    },
                ],
            },
        ];
    }
    // Object - recurse into properties
    for (const [key, value] of Object.entries(output)) {
        const fullKey = prefix ? `${prefix}.${key}` : key;
        const isArray = Array.isArray(value);
        const valueType = typeof value;
        const varDef = {
            key: fullKey,
            name: key,
            type: isArray
                ? VariableType.Array
                : valueType === 'object'
                    ? VariableType.Object
                    : valueType,
            groupKey: VariableGroupKey.Fields,
            isArray,
            extra
        };
        // Handle arrays with itemSchema
        if (isArray && value.length > 0) {
            const firstItem = value[0];
            // Use helper function to recursively build itemSchema
            const itemSchema = buildItemSchema(firstItem, extra);
            varDef.extra = Object.assign(Object.assign({}, extra), { itemSchema });
            // Add length property
            varDef.children = [
                {
                    key: `${fullKey}.length`,
                    name: 'Length',
                    type: VariableType.Number,
                    groupKey: VariableGroupKey.Meta,
                    extra: Object.assign(Object.assign({}, extra), { description: 'Number of items' }),
                },
            ];
        }
        else if (!isArray && valueType === 'object' && value !== null) {
            // Regular object - recurse into children
            varDef.children = genGeneralVariables(value, fullKey, extra);
        }
        variables.push(varDef);
    }
    return variables;
}
//# sourceMappingURL=data:application/json;base64,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